import { useState, type FormEvent } from 'react';
import { changePassword } from '../api/auth';
import './ChangePasswordModal.css';

interface Props {
  onClose: () => void;
}

export function ChangePasswordModal({ onClose }: Props) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);

    if (next.length < 10) {
      setError('New password must be at least 10 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (next === current) {
      setError('New password must differ from the current password.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(resolveError(err));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="change-pw-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="change-pw-card glass" onSubmit={onSubmit}>
        <header className="change-pw-card__head">
          <h2 className="change-pw-card__title">Change password</h2>
          <button
            type="button"
            className="change-pw-card__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {done ? (
          <div className="change-pw-card__success">
            Password updated. Closing...
          </div>
        ) : (
          <>
            <label className="change-pw-field">
              <span className="change-pw-field__label">Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                disabled={submitting}
                required
                autoFocus
              />
            </label>

            <label className="change-pw-field">
              <span className="change-pw-field__label">New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                disabled={submitting}
                required
                minLength={10}
              />
              <span className="change-pw-field__hint">
                At least 10 characters. Longer is better.
              </span>
            </label>

            <label className="change-pw-field">
              <span className="change-pw-field__label">Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                required
                minLength={10}
              />
            </label>

            {error && <div className="change-pw-card__error">{error}</div>}

            <div className="change-pw-card__actions">
              <button
                type="button"
                className="change-pw-card__cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="change-pw-card__submit"
                disabled={submitting || !current || !next || !confirm}
              >
                {submitting ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function resolveError(err: unknown): string {
  if (!(err instanceof Error)) return 'Update failed. Try again.';
  const msg = err.message;
  if (msg.includes('401')) return 'Current password is incorrect.';
  if (msg.includes('400')) return 'Request was rejected. Check the form values.';
  if (msg.includes('429')) return 'Too many attempts. Wait a moment.';
  return 'Update failed. Please try again.';
}
